import type { Website, Company } from 'types';
import { useState, useEffect, startTransition } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClient } from 'hooks/useClient';
import { useAuth } from 'hooks/useAuth';
import { useQueryState } from 'hooks/useQueryState';

/**
 * Deliberately silent on a bad value, and the one place in this pass that is.
 *
 * `JSON.parse` on this key was unguarded in two places. A hand-edited or truncated
 * localStorage entry threw a SyntaxError - from inside an effect on mount, which took the
 * whole client list down, and from the Active switch, where it produced a toast reading
 * "Unexpected token" that no user could act on.
 *
 * There is nothing to tell anyone here: this is a local view preference, not entered data,
 * and "every client shown" is the correct reading of a value we cannot understand. The next
 * toggle rewrites the key, so it self-heals.
 */
const readDisabledAccountClients = (): Set<number> => {
  const stored = localStorage.getItem('DISABLED_ACCOUNT_CLIENTS');
  if (!stored) return new Set();
  try {
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? new Set(parsed as number[]) : new Set();
  } catch {
    return new Set();
  }
};

/**
 * The global query-state key holding the company every screen is scoped to: an agency user's
 * chosen client, anyone else's own company. useBetaFeature reads it too.
 */
export const ORG_SCOPE_KEY = 'org';

export const useOrganizations = () => {
  const [clientAccount, setClientAccount] = useQueryState<number>(ORG_SCOPE_KEY, 0, true);
  const [website, setWebsite] = useQueryState<number>('ws', 0, true);
  const [searchText, setSearchText] = useQueryState<string>('searchText', '');
  const [options, setOptions] = useState<SelectOption[]>();
  const [filteredOrgs, setFilteredOrgs] = useState<Company[]>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const apiClient = useClient();

  useEffect(() => {
    if (!!user && user.company.type !== 'Agency') {
      setClientAccount(user.company.id);
    }
  }, [user]);

  const { data: clientAccounts } = useQuery({
    queryKey: ['CLIENT_ACCOUNTS'],
    queryFn: () =>
      user &&
      (user.company.type === 'Agency'
        ? apiClient.get<Company[]>(`/companies/${user.company.id}/clients`)
        : [user.company]),
    enabled: !!user,
  });

  useEffect(() => {
    if (clientAccounts) {
      const disabledAccountClients = readDisabledAccountClients();

      const options = clientAccounts
        .filter((org: any) => !disabledAccountClients.has(org.id))
        .map((org: any) => ({ value: org.id, label: org.friendlyName }));

      setOptions(options);
      setFilteredOrgs(clientAccounts);
      if (!clientAccount || !options.find(({ value }: any) => value === clientAccount)) {
        const searchParams = new URLSearchParams(window.location.search);
        const qsOrg = searchParams.get('org');
        const qsWebsite = searchParams.get('ws')!;
        const selectedClientAccount: Company =
          (qsOrg && clientAccounts.find((ca: any) => ca.id === (+qsOrg || options[0]?.value))) || clientAccounts[0];

        if (selectedClientAccount) {
          const selectedWebsite: Website | undefined = selectedClientAccount.websites.find((ws: any) => {
            if (qsWebsite && ws.id === +qsWebsite) return ws;
          });
          setClientAccount(selectedClientAccount.id);
          setWebsite(selectedWebsite?.id ?? selectedClientAccount.websites[0]?.id ?? 0);
        }
      }

      clientAccounts.forEach((org: any) => {
        org.active = !disabledAccountClients.has(org.id);
      });
    }
  }, [clientAccounts]);

  const toggleOrganization = (organization: any) => (checked: boolean) => {
    const disabledAccountClients = readDisabledAccountClients();
    if (checked) {
      disabledAccountClients.delete(organization.id);
    } else {
      disabledAccountClients.add(organization.id);
    }
    localStorage.setItem('DISABLED_ACCOUNT_CLIENTS', JSON.stringify([...disabledAccountClients]));
    queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] });
  };

  /** Every billing query (state, usage, client usage, timeseries, invoices) is keyed `BILLING_*`. */
  const invalidateBillingQueries = () =>
    queryClient.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('BILLING_'),
    });

  /**
   * The `!user` guard used to `return`, so Create New Client did nothing at all if the
   * session had lapsed - no client, no error, a dialog that looked ignored.
   */
  const createOrganization = async (payload: any) => {
    if (!user) {
      throw new Error('Your session has expired. Sign in again to create a client.');
    }
    if (!payload.name?.trim()) {
      throw new Error('Enter a client name.');
    }
    const { id: parentCompanyId } = user.company;
    const newOrganization = await apiClient.post('/companies', { ...payload, parentCompanyId });
    queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] });
    // A new client is listed on the agency's billing page from the start, with no plan.
    // Each client has a plan of its own, so nothing serves for it until one is chosen there.
    invalidateBillingQueries();
    setTimeout(() => setClientAccount(newOrganization.id), 50);
  };

  /**
   * Removes the company. The server refuses with a 502 (and a message saying so)
   * if a live subscription it holds could not be cancelled; the caller shows it.
   */
  const removeOrganization = async (id: number) => {
    await apiClient.delete(`/companies/${id}`);
    queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] });
    invalidateBillingQueries();
  };

  useEffect(() => {
    if (clientAccounts) {
      setFilteredOrgs(
        clientAccounts.filter((acc: any) => acc.friendlyName?.toLowerCase().includes(searchText?.toLowerCase())),
      );
    }
  }, [clientAccounts, searchText]);

  const changeOrganization = (id: number) => {
    const selectedClientAccount = clientAccounts?.find((ca: any) => ca.id === id);
    if (!selectedClientAccount) return;
    startTransition(() => {
      setClientAccount(selectedClientAccount.id);
      setWebsite(selectedClientAccount.websites[0]?.id);
    });
  };

  const changeFriendlyName = async (id: number, friendlyName: string) => {
    await apiClient.put(`/companies/${id}`, { friendlyName });
    queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] });
  };

  const updateOrganization = async (companyId: number, payload: any) => {
    await apiClient.put(`/companies/${companyId}`, payload);
  };

  return {
    organizations: clientAccounts,
    organization: clientAccounts?.find((ca: any) => ca.id === clientAccount),
    website,
    setWebsite,
    filteredOrgs,
    options,
    setOrganization: changeOrganization,
    createOrganization,
    removeOrganization,
    changeFriendlyName,
    updateOrganization,
    toggleOrganization,
    searchText,
    setSearchText,
  };
};
